import Seo from '@/components/Seo';
import SiteHeader from '@/components/site/SiteHeader';
import SiteFooter from '@/components/site/SiteFooter';
import Hover from '@/components/site/Hover';
import Field from '@/components/site/Field';
import Turnstile from '@/components/Turnstile';
import { css } from '@/components/site/css';
import { useContactForm } from '@/components/site/useContactForm';

const inputStyle =
  'width:100%;font-family:inherit;font-size:15px;color:#fff;background:#24276C;border:1px solid #3A3D82;border-radius:10px;padding:13px 14px';
const inputFocus = 'border-color:#E9402B;outline:none';
const labelStyle = 'display:block;font-size:13px;font-weight:600;color:#B9C0DC;margin-bottom:7px';

/** Airport & venue concessionaires page. Ported verbatim from Airport.dc.html.
 *  Hero-card and beverage sections (editor toggles, default on) always render.
 *  "Request a demo" CTAs scroll to the on-page form (#demo). */
export default function AirportPage() {
  const { submitted, submitting, error, setToken, resetSignal, turnstileEnabled, onSubmit } =
    useContactForm('airport');

  return (
    <div
      className="site-shell"
      style={css(
        "font-family:'Hanken Grotesk',system-ui,sans-serif;color:#24276C;background:#ffffff;-webkit-font-smoothing:antialiased;overflow-x:clip"
      )}
    >
      <Seo
        title="F&B Insights — Street pricing data for airport & venue concessionaires"
        description="Native, verifiable street pricing for the local markets around your captive venues. Meet your lease's street-pricing requirement and stay competitive, location by location."
        path="/airport"
      />

      <SiteHeader
        navLinks={[
          { label: 'The requirement', href: '#requirement' },
          { label: 'What we provide', href: '#capability' },
          { label: 'Beverage intelligence', href: '#beverage' },
        ]}
        cta={{ label: 'Request a demo', href: '#demo', bg: '#24276C', hoverBg: '#1A1C50' }}
      />

      <span id="top" />

      {/* Hero */}
      <section style={css('background:#24276C')}>
        <div
          className="site-hero-grid"
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px 72px;display:grid;grid-template-columns:minmax(0,1.08fr) minmax(0,0.92fr);gap:60px;align-items:center'
          )}
        >
          <div>
            <div
              style={css(
                'display:inline-flex;align-items:center;gap:9px;font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:22px'
              )}
            >
              <span
                style={css(
                  'width:7px;height:7px;border-radius:50%;background:#E9402B;display:inline-block'
                )}
              ></span>
              For Airport &amp; Venue Concessionaires
            </div>
            <h1
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(38px,4.8vw,58px);line-height:1.05;font-weight:800;letter-spacing:-0.02em;margin:0 0 24px;color:#ffffff;text-wrap:balance"
              )}
            >
              Price to the local street market. And prove it.
            </h1>
            <p
              style={css(
                'font-size:20px;line-height:1.5;color:#B9C0DC;margin:0 0 34px;max-width:540px;text-wrap:pretty'
              )}
            >
              Your leases require pricing in line with the surrounding street market, across every
              captive venue you operate. Meeting that requirement, and staying competitive within
              it, both depend on accurate local pricing you can stand behind. We provide that data:
              real, native, verifiable street pricing for the markets around your locations.
            </p>
            <div className="site-hero-cta" style={css('display:flex;flex-wrap:wrap;gap:14px')}>
              <Hover
                href="#demo"
                style={css(
                  'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('background:#D3341F')}
              >
                Request a demo<span style={css('font-size:18px;line-height:1')}>→</span>
              </Hover>
              <Hover
                href="#capability"
                style={css(
                  'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#fff;background:transparent;border:1.5px solid rgba(255,255,255,0.35);padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('border-color:#ffffff')}
              >
                See what we provide
              </Hover>
            </div>
          </div>

          {/* Hero card (editor toggle showHeroCard, default on) */}
          <div style={css('position:relative')}>
            <div
              style={css(
                'position:absolute;inset:-18px -18px auto auto;width:76px;height:76px;border-radius:16px;background:#E9402B;opacity:0.1'
              )}
            ></div>
            <div
              style={css(
                'position:relative;background:#fff;border:1px solid #E6E4DE;border-radius:18px;box-shadow:0 24px 60px -28px rgba(36,39,108,0.32);padding:26px'
              )}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:15px;font-weight:700;color:#24276C"
                  )}
                >
                  Street comps · terminal trade area
                </span>
                <span
                  style={css(
                    'font-size:12px;font-weight:600;color:#12A150;background:#EAF7EF;padding:5px 10px;border-radius:20px'
                  )}
                >
                  Refreshed monthly
                </span>
              </div>
              <div style={css('display:flex;flex-direction:column;gap:0')}>
                <div
                  style={css(
                    'display:grid;grid-template-columns:1.4fr 1fr 0.9fr;gap:10px;padding:0 0 10px;font-size:12px;font-weight:600;color:#8A8A93;text-transform:uppercase;letter-spacing:0.03em'
                  )}
                >
                  <span>Item</span>
                  <span>Street avg</span>
                  <span>Your price</span>
                </div>
                {[
                  ['Old Fashioned', '$15.50', '$16.00 · in line', '#12A150'],
                  ['Draft IPA, 16oz', '$9.00', '$10.50 · above', '#C58A00'],
                  ['Cheeseburger', '$17.25', '$17.00 · in line', '#12A150'],
                  ['House Cabernet', '$13.00', '$13.00 · in line', '#12A150'],
                ].map(([item, avg, price, color]) => (
                  <div
                    key={item}
                    style={css(
                      'display:grid;grid-template-columns:1.4fr 1fr 0.9fr;gap:10px;padding:12px 0;border-top:1px solid #F0EFEB;align-items:center'
                    )}
                  >
                    <span style={css('font-size:14px;color:#24276C;font-weight:600')}>{item}</span>
                    <span style={css('font-size:14px;color:#54545E')}>{avg}</span>
                    <span style={css(`font-size:13px;font-weight:600;color:${color}`)}>
                      {price}
                    </span>
                  </div>
                ))}
              </div>
              <div
                style={css(
                  'margin-top:16px;padding-top:14px;border-top:1px solid #F0EFEB;font-size:12px;color:#8A8A93;display:flex;align-items:center;gap:8px'
                )}
              >
                <span style={css('color:#E9402B;font-weight:700')}>↳</span>
                Every street price sourced from real dine-in menus, dated and traceable.
              </div>
            </div>
            <div
              style={css(
                'position:relative;text-align:center;margin-top:12px;font-size:12px;color:#94A0C4'
              )}
            >
              Illustrative view.
            </div>
          </div>
        </div>
      </section>

      {/* Requirement */}
      <section
        id="requirement"
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:74px 28px')}>
          <div style={css('max-width:720px;margin-bottom:42px')}>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
              )}
            >
              The requirement
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,42px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#24276C"
              )}
            >
              Street pricing at concession scale is only as good as the data behind it.
            </h2>
            <p style={css('font-size:18px;line-height:1.55;color:#54545E;margin:0')}>
              Concession leases typically tie your prices to the surrounding local street market,
              and ask you to demonstrate it. That&rsquo;s a hard standard to meet across every
              location when the underlying pricing data can&rsquo;t be trusted.
            </p>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:22px'
            )}
          >
            {[
              [
                'The standard is local',
                'Prices must reflect the street market around each location, not a national average. Every venue is its own trade area.',
              ],
              [
                'You have to stand behind it',
                "It isn't enough to price competitively. You need pricing you can defend, backed by data that holds up to scrutiny.",
              ],
              [
                "Scraped data can't carry it",
                "Scraped third-party menu prices, from aggregator sites and delivery apps, are marked up and stale. They're the wrong foundation for a requirement this consequential.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                style={css(
                  'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:28px 26px'
                )}
              >
                <div
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:18px;font-weight:700;color:#24276C;margin-bottom:10px"
                  )}
                >
                  {title}
                </div>
                <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capability */}
      <section id="capability" style={css('max-width:1180px;margin:0 auto;padding:80px 28px 20px')}>
        <div style={css('display:flex;align-items:center;gap:14px;margin-bottom:16px')}>
          <span
            style={css(
              'display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#12A150;background:#EAF7EF;padding:7px 14px;border-radius:20px'
            )}
          >
            <span style={css('width:7px;height:7px;border-radius:50%;background:#12A150')}></span>
            Live and operating at national scale
          </span>
        </div>
        <h2
          style={css(
            "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,3.6vw,44px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#24276C;max-width:760px"
          )}
        >
          The trustworthy street data your pricing depends on.
        </h2>
        <p
          style={css(
            'font-size:19px;line-height:1.55;color:#54545E;margin:0 0 44px;max-width:660px'
          )}
        >
          Native, first-party menu and pricing data for the local markets around your locations.
          Real dine-in street prices, sourced directly, not scraped estimates you&rsquo;d have to
          discount.
        </p>
        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:22px'
          )}
        >
          {[
            [
              'Native, first-party pricing',
              'Real dine-in street pricing around each captive venue, collected at the source, not pulled from delivery apps.',
            ],
            [
              'Item-level detail',
              'Item names, descriptions, prices, menu types, and streetside tags. The granularity real comparisons require.',
            ],
            [
              'Coverage by trade area',
              'Each market defined by the local trade area around the terminal, refreshed monthly so the picture stays current.',
            ],
            [
              'A platform your teams use',
              'Corporate and local operators search and filter competitive benchmarks across markets in one place.',
            ],
            [
              'Refreshed monthly',
              'Every market is re-collected monthly, so your benchmarks reflect the current street market, not a snapshot from last year.',
            ],
          ].map(([title, body]) => (
            <div
              key={title}
              style={css(
                'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:30px 28px'
              )}
            >
              <div
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:19px;font-weight:700;margin:0 0 10px;color:#24276C"
                )}
              >
                {title}
              </div>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Beverage intelligence (editor toggle showBeverage, default on) */}
      <section id="beverage" style={css('max-width:1180px;margin:0 auto;padding:70px 28px')}>
        <div
          className="site-panel"
          style={css(
            'background:#24276C;border-radius:22px;padding:52px 48px;color:#fff;position:relative;overflow:hidden'
          )}
        >
          <div
            style={css(
              'position:absolute;top:0;right:0;width:220px;height:220px;background:#E9402B;opacity:0.12;border-radius:0 0 0 100%'
            )}
          ></div>
          <div
            style={css(
              'position:relative;display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:48px;align-items:center'
            )}
          >
            <div>
              <div
                style={css(
                  'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:14px'
                )}
              >
                Beverage intelligence
              </div>
              <h2
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.2vw,40px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#fff"
                )}
              >
                The deepest read on what airport bars actually pour.
              </h2>
              <p style={css('font-size:18px;line-height:1.55;color:#B9C0DC;margin:0')}>
                Premium pours and cocktails drive airport bar economics, so we go deeper than a
                price. Cocktail composition and detailed spirit attributes give you intelligence
                scraped data and generalist competitors can&rsquo;t match.
              </p>
            </div>
            <div
              style={css(
                'background:#1A1C50;border:1px solid #35387E;border-radius:16px;padding:26px'
              )}
            >
              <div
                style={css(
                  'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#8B93B8;margin-bottom:16px'
                )}
              >
                One pour, fully described
              </div>
              <div style={css('display:flex;flex-direction:column;gap:0')}>
                {[
                  ['Cocktail', 'Manhattan', false],
                  ['Base spirit', 'Rye whiskey', false],
                  ['Composition', 'Rye · sweet vermouth · bitters', false],
                  ['Tier', 'Premium', false],
                  ['Street price', '$17.00', true],
                ].map(([label, value, bold]) => (
                  <div
                    key={label as string}
                    style={css(
                      'display:flex;justify-content:space-between;gap:16px;padding:11px 0;border-top:1px solid #35387E'
                    )}
                  >
                    <span style={css('font-size:14px;color:#9AA2C4;flex:none')}>{label}</span>
                    <span
                      style={css(
                        `font-size:14px;color:#fff;font-weight:${bold ? '700' : '600'};text-align:right`
                      )}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two sides */}
      <section style={css('max-width:1180px;margin:0 auto;padding:20px 28px 70px')}>
        <div style={css('max-width:680px;margin-bottom:42px')}>
          <div
            style={css(
              'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
            )}
          >
            Two sides of the same data
          </div>
          <h2
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,40px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
            )}
          >
            Meet the requirement. Then win on price.
          </h2>
        </div>
        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:22px'
          )}
        >
          <div
            style={css(
              'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:34px 32px;border-top:4px solid #E9402B'
            )}
          >
            <div
              style={css(
                'display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:700;color:#E9402B;background:#FBEAE7;padding:5px 11px;border-radius:20px;margin-bottom:20px'
              )}
            >
              The urgent hook
            </div>
            <h3
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:22px;font-weight:700;margin:0 0 12px;color:#24276C"
              )}
            >
              Meet your street-pricing requirement
            </h3>
            <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>
              Price in line with each local street market and back it with data you can stand
              behind, location by location. Native first-party data is the only kind trustworthy
              enough for a standard this consequential.
            </p>
          </div>
          <div
            style={css(
              'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:34px 32px;border-top:4px solid #24276C'
            )}
          >
            <div
              style={css(
                'display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:700;color:#24276C;background:#EDEDF3;padding:5px 11px;border-radius:20px;margin-bottom:20px'
              )}
            >
              The sustained value
            </div>
            <h3
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:22px;font-weight:700;margin:0 0 12px;color:#24276C"
              )}
            >
              Price competitively across every market
            </h3>
            <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>
              The same trusted intelligence powers ongoing competitive benchmarking, so corporate
              and local teams keep every location priced right as the market moves.
            </p>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:64px 28px;text-align:center')}>
          <div
            style={css(
              "font-size:34px;line-height:1;color:#C9D0E6;font-family:'Schibsted Grotesk',sans-serif;margin-bottom:20px"
            )}
          >
            &ldquo;
          </div>
          <p
            style={css(
              "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(22px,2.6vw,30px);line-height:1.3;font-weight:600;letter-spacing:-0.01em;color:#24276C;margin:0 auto;max-width:840px;text-wrap:balance"
            )}
          >
            The largest airport concessionaire relies on F&amp;B Insights as the backbone of their
            competitive pricing and menu strategy across their airport markets.
          </p>
          <div
            style={css(
              'margin-top:26px;font-size:14px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#8A8A93'
            )}
          >
            Trusted where the data has to be right
          </div>
        </div>
      </section>

      {/* Demo form */}
      <section id="demo" style={css('background:#24276C;color:#fff')}>
        <div
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px;display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:56px;align-items:start'
          )}
        >
          <div>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:14px'
              )}
            >
              Talk to our team
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,3.6vw,44px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 0 20px;color:#fff"
              )}
            >
              See coverage for your markets.
            </h2>
            <p
              style={css(
                'font-size:18px;line-height:1.55;color:#B9C0DC;margin:0 0 28px;max-width:460px'
              )}
            >
              Tell us which captive venue you operate and we&rsquo;ll walk you through the street
              data and beverage intelligence available for those markets.
            </p>
            <div style={css('display:flex;flex-direction:column;gap:14px')}>
              {[
                'Native, verifiable street pricing you can stand behind',
                'Item-level comps, refreshed monthly',
                'Deep beverage and cocktail intelligence',
              ].map((t) => (
                <div
                  key={t}
                  style={css(
                    'display:flex;gap:12px;align-items:center;font-size:16px;color:#EAECF6'
                  )}
                >
                  <span style={css('color:#F0806F;font-weight:700')}>✓</span>
                  {t}
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="site-form"
            style={css(
              'background:#1A1C50;border:1px solid #35387E;border-radius:18px;padding:30px'
            )}
          >
            <div style={css('display:flex;flex-direction:column;gap:16px')}>
              <div>
                <label style={css(labelStyle)}>Name</label>
                <Field
                  name="name"
                  required
                  placeholder="Your name"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>Company</label>
                <Field
                  name="company"
                  required
                  placeholder="Concession operator"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>Work email</label>
                <Field
                  type="email"
                  name="email"
                  required
                  placeholder="you@company.com"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>
                  Airports / markets you operate{' '}
                  <span style={css('color:#7581AB;font-weight:500')}>(optional)</span>
                </label>
                <Field
                  as="textarea"
                  name="markets"
                  rows={2}
                  placeholder="e.g. ORD, DFW, ATL"
                  style={css(`${inputStyle};resize:vertical`)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              {turnstileEnabled && (
                <Turnstile
                  onVerify={setToken}
                  onExpire={() => setToken('')}
                  resetSignal={resetSignal}
                  theme="dark"
                  size="flexible"
                />
              )}
              <Hover
                as="button"
                type="submit"
                style={css(
                  `margin-top:4px;font-family:inherit;font-size:16px;font-weight:600;color:#fff;background:#E9402B;border:none;border-radius:10px;padding:15px;cursor:pointer${submitting ? ';opacity:0.6' : ''}`
                )}
                hoverStyle={css('background:#D3341F')}
              >
                {submitting ? 'Sending…' : 'Request a demo'}
              </Hover>
              {error && (
                <div style={css('font-size:13px;color:#F0806F;text-align:center')}>{error}</div>
              )}
              {submitted && (
                <div style={css('font-size:14px;color:#8FE0A8;text-align:center')}>
                  Thanks. Our team will reach out to set up your walkthrough.
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      <SiteFooter
        navLinks={[
          { label: 'Home', href: '/' },
          { label: 'The requirement', href: '#requirement' },
          { label: 'What we provide', href: '#capability' },
          { label: 'Request a demo', href: '#demo' },
        ]}
      />
    </div>
  );
}
